import {
  compareDeadlineQuarterKeys,
  getDeadlineQuarterKey,
} from '@griply/utils';
import { useMemo } from 'react';
import {
  rootId,
  TaskListItemTypes,
  TreeItem,
} from 'shared/components/ui/virtualized-tree-list';
import { Goal } from 'shared/types/goal';
import { ID } from 'shared/types/id';

import { ARCHIVED_SECTION_ID } from '../constants';

export type Options = {
  hideArchived?: boolean;
  hideCompleted?: boolean;
};

// Quarter sections use the quarter key (e.g. '2026-Q1' / 'no-deadline') directly
// as their tree id; goal ids are Firestore ids and never collide with these.
export const useDeadlineGoalListTree = (
  goals: Goal[],
  { hideArchived, hideCompleted }: Options,
) =>
  useMemo<Record<ID, TreeItem>>(() => {
    const activeGoals: Goal[] = [];
    const archivedGoals: Goal[] = [];

    goals.forEach((goal) => {
      if (goal.archivedAt) {
        if (!hideArchived) {
          archivedGoals.push(goal);
        }
        return;
      }
      if (!hideCompleted || !goal.completedAt) {
        activeGoals.push(goal);
      }
    });

    const treeItems: Record<ID, TreeItem> = {};

    goals.forEach((goal) => {
      treeItems[goal.id] = {
        id: goal.id,
        type: TaskListItemTypes.Goal,
        name: goal.name,
        children: [],
        isFolder: true,
      };
    });

    const quarterKeys = new Set<string>();

    activeGoals.forEach((goal) => {
      const parentIds = goal.parentIds ?? [];
      const parentId = [...parentIds].reverse().find((id) => treeItems[id]);

      if (parentId) {
        treeItems[parentId].children?.push(goal.id);
        return;
      }

      const quarterKey = getDeadlineQuarterKey(goal.deadline);
      if (!treeItems[quarterKey]) {
        treeItems[quarterKey] = {
          id: quarterKey,
          type: TaskListItemTypes.LifeArea,
          name: quarterKey,
          children: [],
          isFolder: true,
        };
        quarterKeys.add(quarterKey);
      }
      treeItems[quarterKey].children?.push(goal.id);
    });

    if (archivedGoals.length) {
      treeItems[ARCHIVED_SECTION_ID] = {
        id: ARCHIVED_SECTION_ID,
        type: TaskListItemTypes.LifeArea,
        name: 'Archived',
        children: archivedGoals.map((goal) => goal.id),
        isFolder: true,
      };
    }

    const rootChildren = [...quarterKeys].sort(compareDeadlineQuarterKeys);

    if (archivedGoals.length) {
      rootChildren.push(ARCHIVED_SECTION_ID);
    }

    return {
      [rootId]: {
        id: rootId,
        name: 'Root',
        type: TaskListItemTypes.Root,
        children: rootChildren,
      },
      ...treeItems,
    };
  }, [goals, hideArchived, hideCompleted]);
